<div class="heading py-5 container">
  <h1 class="bold font-curve-large">Tabs</h1>
  <app-dependency-link documentationPath="components/nav"></app-dependency-link>
</div>

<section>
  <div class="my-5">
    <div class="container">
      <h3 class="bold">Default</h3>
      <p class="font-curve-regular light">
        The tabs pattern is designed to be used outside of a
        <code>.container</code>
        context to spread the background and lines to the edge of the viewport. However, the tabs
        can be used in another context as well (see at the bottom). In this case, notice the missing
        <code>.container</code>
        classes in the template.
      </p>
    </div>
    <app-ngb-tabs-demo></app-ngb-tabs-demo>
    <div class="container">
      <code class="block" [highlight]="defaultSnippet" [languages]="['html']"></code>
    </div>
  </div>
  <div class="bg-nightblue mt-5 py-5">
    <div class="container">
      <h3 class="bold">Dark background</h3>
    </div>
    <app-ngb-tabs-demo></app-ngb-tabs-demo>
    <div class="container">
      <code class="block" [highlight]="darkBgSnippet" [languages]="['html']"></code>
    </div>
  </div>
  <div class="bg-light mb-5 py-5">
    <div class="container">
      <h3 class="bold">Light background</h3>
    </div>
    <app-ngb-tabs-demo></app-ngb-tabs-demo>
    <div class="container">
      <code class="block" [highlight]="lightBgSnippet" [languages]="['html']"></code>
    </div>
  </div>
  <div class="my-5">
    <div class="container">
      <h3 class="bold">Colored tabs on white background</h3>
    </div>
    <app-ngb-tabs-demo wrapperBg="white" contentBg="coral" tabsBg="coral"></app-ngb-tabs-demo>
    <div class="bg-coral pb-5">
      <div class="container">
        <h4 class="bold">Heads up!</h4>
        <p class="font-curve-regular light">
          Notice the three
          <code>bg-*</code>
          classes to see how to fine-tune the background colors.
        </p>
        <code class="block" [highlight]="colorBgSnippet" [languages]="['html']"></code>
      </div>
    </div>
  </div>

  <div class="my-5 container">
    <h3 class="bold">
      Tabs inside a
      <code>.container</code>
      context
    </h3>
    <div class="tabs-wrapper">
      <ul ngbNav #nav="ngbNav" class="tabs">
        <li ngbNavItem>
          <a href="" ngbNavLink class="tab-title">Inside job</a>
          <ng-template ngbNavContent ngPreserveWhitespaces>
            <div class="row">
              <div class="col-md-4">
                <img src="https://i.imgur.com/676x4D5.jpg" alt="" />
              </div>
              <div class="col-md-8">
                <h3 class="bold h2 mb-4">Inside job</h3>
                <p>
                  <i><strong>Inside Job</strong></i>
                  is a 2010 American
                  <a href="/wiki/Documentary_film" title="Documentary film">documentary film</a>
                  , directed by
                  <a
                    href="/wiki/Charles_H._Ferguson"
                    class="mw-redirect"
                    title="Charles H. Ferguson"
                  >
                    Charles Ferguson
                  </a>
                  , about the
                  <a
                    href="/wiki/Late-2000s_financial_crisis"
                    class="mw-redirect"
                    title="Late-2000s financial crisis"
                  >
                    late-2000s financial crisis
                  </a>
                  . Ferguson, who began researching in 2008,
                  <sup id="cite_ref-3" class="reference"><a href="#cite_note-3">[3]</a></sup>
                  says the film is about "the systemic corruption of the United States by the
                  <a href="/wiki/Financial_services" title="Financial services">
                    financial services
                  </a>
                  industry and the consequences of that systemic corruption".
                  <sup id="cite_ref-4" class="reference"><a href="#cite_note-4">[4]</a></sup>
                  In five parts, the film explores how changes in the policy environment and banking
                  practices helped create the financial crisis.
                </p>
                <p>
                  <i>Inside Job</i>
                  was acclaimed by film critics, who praised its pacing, research and exposition of
                  complex material. It screened at the
                  <a href="/wiki/2010_Cannes_Film_Festival" title="2010 Cannes Film Festival">
                    2010 Cannes Film Festival
                  </a>
                  in May and won the 2010
                  <a
                    href="/wiki/Academy_Award_for_Best_Documentary_Feature"
                    title="Academy Award for Best Documentary Feature"
                  >
                    Academy Award for Best Documentary Feature
                  </a>
                  .
                </p>
              </div>
            </div>
          </ng-template>
        </li>
        <li ngbNavItem>
          <a href="" ngbNavLink class="tab-title">Inside out</a>
          <ng-template ngbNavContent ngPreserveWhitespaces>
            <div class="row">
              <div class="col-md-4">
                <img src="https://i.imgur.com/c4LLjQZ.jpg" alt="" />
              </div>
              <div class="col-md-8">
                <h3 class="bold h2 mb-4">Inside out</h3>
                <p>
                  <i><strong>Inside Out</strong></i>
                  is a 2015 American
                  <a href="/wiki/Computer-animated" class="mw-redirect" title="Computer-animated">
                    computer-animated
                  </a>
                  <a href="/wiki/Comedy_film" title="Comedy film">comedy film</a>
                  produced by
                  <a
                    href="/wiki/Pixar_Animation_Studios"
                    class="mw-redirect"
                    title="Pixar Animation Studios"
                  >
                    Pixar Animation Studios
                  </a>
                  and released by
                  <a href="/wiki/Walt_Disney_Pictures" title="Walt Disney Pictures">
                    Walt Disney Pictures
                  </a>
                  . The film was directed by
                  <a href="/wiki/Pete_Docter" title="Pete Docter">Pete Docter</a>
                  and co-directed by
                  <a href="/wiki/Ronnie_del_Carmen" title="Ronnie del Carmen">Ronnie del Carmen</a>
                  , with a screenplay written by Docter,
                  <a href="/wiki/Meg_LeFauve" title="Meg LeFauve">Meg LeFauve</a>
                  , and
                  <a href="/wiki/Josh_Cooley" title="Josh Cooley">Josh Cooley</a>
                  , adapted from a story by Docter and del Carmen. The film is set in the mind of a
                  young girl named Riley (
                  <a href="/wiki/Kaitlyn_Dias" title="Kaitlyn Dias">Kaitlyn Dias</a>
                  ), where five personified
                  <a href="/wiki/Emotion#Classification" title="Emotion">emotions</a>
                  —Joy (
                  <a href="/wiki/Amy_Poehler" title="Amy Poehler">Amy Poehler</a>
                  ), Sadness (
                  <a href="/wiki/Phyllis_Smith" title="Phyllis Smith">Phyllis Smith</a>
                  ), Anger (
                  <a href="/wiki/Lewis_Black" title="Lewis Black">Lewis Black</a>
                  ), Fear (
                  <a href="/wiki/Bill_Hader" title="Bill Hader">Bill Hader</a>
                  ), and Disgust (
                  <a href="/wiki/Mindy_Kaling" title="Mindy Kaling">Mindy Kaling</a>
                  )—try to lead her through life as she and her parents (
                  <a href="/wiki/Diane_Lane" title="Diane Lane">Diane Lane</a>
                  and
                  <a href="/wiki/Kyle_MacLachlan" title="Kyle MacLachlan">Kyle MacLachlan</a>
                  ) adjust to their new surroundings after moving from
                  <a href="/wiki/Minnesota" title="Minnesota">Minnesota</a>
                  to
                  <a href="/wiki/San_Francisco" title="San Francisco">San Francisco</a>
                  .
                </p>
                <p>
                  Docter first began developing
                  <i>Inside Out</i>
                  in 2010, after noticing changes in his daughter's personality as she grew older.
                  The film's producers consulted numerous
                  <a href="/wiki/Psychologist" title="Psychologist">psychologists</a>
                  including
                  <a href="/wiki/Dacher_Keltner" title="Dacher Keltner">Dacher Keltner</a>
                  from the
                  <a
                    href="/wiki/University_of_California,_Berkeley"
                    title="University of California, Berkeley"
                  >
                    University of California, Berkeley
                  </a>
                  ,
                  <sup id="cite_ref-DKeltner2015_5-0" class="reference">
                    <a href="#cite_note-DKeltner2015-5">[5]</a>
                  </sup>
                  who helped revise the story by emphasizing the
                  <a href="/wiki/Neuropsychological" class="mw-redirect" title="Neuropsychological">
                    neuropsychological
                  </a>
                  findings that human
                  <a href="/wiki/Emotion" title="Emotion">emotions</a>
                  affect
                  <a href="/wiki/Interpersonal_relationship" title="Interpersonal relationship">
                    interpersonal relationships
                  </a>
                  and can be significantly moderated by them.
                  <sup id="cite_ref-science_6-0" class="reference">
                    <a href="#cite_note-science-6">[6]</a>
                  </sup>
                </p>
                <p>
                  After premiering at the
                  <a
                    href="/wiki/68th_Cannes_Film_Festival"
                    class="mw-redirect"
                    title="68th Cannes Film Festival"
                  >
                    68th Cannes Film Festival
                  </a>
                  on May 18, 2015,
                  <i>Inside Out</i>
                  was released in North America on June 19, 2015, accompanied by the short film
                  <i><a href="/wiki/Lava_(2014_film)" title="Lava (2014 film)">Lava</a></i>
                  . The film was praised for its concept, screenplay, subject matter,
                  <a href="/wiki/Michael_Giacchino" title="Michael Giacchino">Michael Giacchino</a>
                  's musical score, and the vocal performances (particularly those of Poehler,
                  Smith, Black, and
                  <a href="/wiki/Richard_Kind" title="Richard Kind">Richard Kind</a>
                  ). The film grossed $90.4 million in its first weekend, making it the highest
                  opening for an original title at the time,
                  <sup id="cite_ref-EWBiggestOriginal_7-0" class="reference">
                    <a href="#cite_note-EWBiggestOriginal-7">[7]</a>
                  </sup>
                  accumulating over $857 million in worldwide box office revenue in 2015, making it
                  the
                  <a href="/wiki/2015_in_film#Highest-grossing_films" title="2015 in film">
                    seventh-highest-grossing film of 2015
                  </a>
                  . The film received several awards, including a
                  <a
                    href="/wiki/BAFTA_Award_for_Best_Animated_Film"
                    title="BAFTA Award for Best Animated Film"
                  >
                    BAFTA Award
                  </a>
                  ,
                  <a
                    href="/wiki/Golden_Globe_Award_for_Best_Animated_Feature_Film"
                    title="Golden Globe Award for Best Animated Feature Film"
                  >
                    Golden Globe Award
                  </a>
                  ,
                  <a
                    href="/wiki/Critics%27_Choice_Movie_Award_for_Best_Animated_Feature"
                    title="Critics' Choice Movie Award for Best Animated Feature"
                  >
                    Critics' Choice Award
                  </a>
                  ,
                  <a
                    href="/wiki/Annie_Award_for_Best_Animated_Feature"
                    title="Annie Award for Best Animated Feature"
                  >
                    Annie Award
                  </a>
                  ,
                  <a
                    href="/wiki/Satellite_Award_for_Best_Animated_or_Mixed_Media_Feature"
                    title="Satellite Award for Best Animated or Mixed Media Feature"
                  >
                    Satellite Award
                  </a>
                  , and
                  <a
                    href="/wiki/Academy_Award_for_Best_Animated_Feature"
                    title="Academy Award for Best Animated Feature"
                  >
                    Academy Award for Best Animated Feature
                  </a>
                  .
                </p>
              </div>
            </div>
          </ng-template>
        </li>
      </ul>
    </div>

    <div [ngbNavOutlet]="nav" class="tab-content"></div>

    <p class="light font-curve-regular mt-4">
      Notice the
      <code>.container</code>
      around all the tabs and the missing
      <code>.container</code>
      classes on the tabs itself.
    </p>
    <code class="block" [highlight]="containerSnippet" [languages]="['html']"></code>
  </div>
</section>
